import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoLight from '../Assets/Asset 4@300x.png';
import logoDark  from '../Assets/Asset 5@300x.png';
import { useTheme } from '../context/ThemeContext';

const NAV_LINKS = [
    { label: 'Home',         path: '/'         },
    { label: 'About',        path: '/about'    },
    { label: 'Our Services', path: '/services' },
    { label: 'Our Partners', path: '/partners' },
    { label: 'For Rent',     path: '/rent'     },
    { label: 'For Sale',     path: '/sale'     },
    { label: 'Contact Us',   path: '/contact'  },
];

const Navbar: React.FC = () => {
    const [scrolled,  setScrolled]  = useState(false);
    const [menuOpen,  setMenuOpen]  = useState(false);
    const { theme, toggleTheme }    = useTheme();
    const navigate                  = useNavigate();
    const { pathname }              = useLocation();
    const isDark = theme === 'dark';

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { setMenuOpen(false); }, [pathname]);

    const goTo = (path: string) => {
        navigate(path);
        setMenuOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const isActive = (path: string) =>
        path === '/' ? pathname === '/' : pathname.startsWith(path);

    const logo = isDark ? logoDark : logoLight;

    return (
        <>
            <style>{`
        /* ── NAV ROOT ── */
        .nav-root {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          background: var(--nav-bg);
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--nav-border);
          transition: background var(--transition), border-color var(--transition), box-shadow var(--transition);
        }
        .nav-root.nav-scrolled { box-shadow: 0 4px 28px rgba(4,0,51,0.08); }
        [data-theme='dark'] .nav-root.nav-scrolled { box-shadow: 0 4px 28px rgba(0,0,0,0.5); }

        .nav-inner {
          display: flex; align-items: center; height: 74px;
          max-width: 1320px; margin: 0 auto; padding: 0 3rem; gap: 2rem;
        }

        /* ── LOGO ── */
        .nav-logo { display: flex; align-items: center; flex-shrink: 0; cursor: pointer; }
        .nav-logo-img {
          height: 48px; width: auto; object-fit: contain;
          display: block; transition: opacity 0.3s;
        }
        .nav-logo-img:hover { opacity: 0.85; }

        /* ── LINKS ── */
        .nav-links {
          display: flex; gap: 2px; list-style: none;
          align-items: center; flex: 1; justify-content: center;
        }
        .nav-link {
          font-family: var(--ff-ui); font-size: 0.63rem; font-weight: 500;
          letter-spacing: 0.15em; text-transform: uppercase;
          color: var(--nav-ink); padding: 8px 13px;
          cursor: pointer; transition: color var(--transition);
          white-space: nowrap; position: relative;
          background: none; border: none;
        }
        .nav-link::after {
          content: ''; position: absolute; bottom: 0; left: 13px; right: 13px;
          height: 2px; background: var(--teal); border-radius: 1px;
          transform: scaleX(0); transform-origin: center;
          transition: transform 0.3s var(--ease);
        }
        .nav-link:hover { color: var(--ink); }
        .nav-link:hover::after { transform: scaleX(1); }
        .nav-link.is-active { color: var(--nav-active); font-weight: 600; }
        .nav-link.is-active::after { transform: scaleX(1); background: var(--teal); }

        /* ══════════════════════════════════════
           THEME ICON BUTTON  (moon / sun)
           ══════════════════════════════════════ */
        .theme-icon-btn {
          width: 44px; height: 44px; border-radius: 10px;
          background: var(--bg2);
          border: 1px solid var(--border-t);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; position: relative; overflow: hidden; flex-shrink: 0;
          transition: border-color 0.3s, box-shadow 0.3s, background 0.3s, transform 0.15s;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .theme-icon-btn:hover {
          border-color: var(--border);
          box-shadow: 0 2px 12px rgba(0,0,0,0.12);
        }
        .theme-icon-btn:active { transform: scale(0.92); }

        /* Both icons sit absolutely centred inside the button */
        .theme-icon-btn svg {
          width: 20px; height: 20px;
          position: absolute;
          fill: none; stroke-width: 1.8;
          stroke-linecap: round; stroke-linejoin: round;
          transition: opacity 0.35s ease,
                      transform 0.42s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Moon — visible in dark mode */
        .t-moon {
          stroke: var(--ink);
          opacity: ${isDark ? '1' : '0'};
          transform: ${isDark ? 'rotate(0deg) scale(1)' : 'rotate(90deg) scale(0.6)'};
        }

        /* Sun — visible in light mode */
        .t-sun {
          stroke: #c88a1a;
          opacity: ${isDark ? '0' : '1'};
          transform: ${isDark ? 'rotate(-90deg) scale(0.6)' : 'rotate(0deg) scale(1)'};
        }

        /* Light-mode border tint */
        [data-theme='light'] .theme-icon-btn {
          background: #fffdf7;
          border-color: rgba(200,138,26,0.22);
        }
        [data-theme='light'] .theme-icon-btn:hover {
          border-color: rgba(200,138,26,0.48);
          box-shadow: 0 2px 14px rgba(200,138,26,0.14);
        }

        /* ── CTA ── */
        .nav-cta {
          flex-shrink: 0; font-family: var(--ff-ui); font-size: 0.6rem;
          letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600;
          color: var(--navy); background: var(--gold);
          padding: 11px 22px; border-radius: var(--r); border: none; cursor: pointer;
          transition: background var(--transition), transform var(--transition), box-shadow var(--transition);
        }
        .nav-cta:hover {
          background: var(--gold-d);
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(200,132,30,0.3);
        }

        /* ── HAMBURGER ── */
        .nav-burger {
          display: none; flex-direction: column; gap: 5px;
          background: none; border: none; padding: 6px;
          margin-left: auto; cursor: pointer;
        }
        .nav-burger span {
          display: block; width: 22px; height: 1.5px;
          background: var(--nav-ink); border-radius: 2px;
          transition: transform 0.35s var(--ease), opacity 0.3s ease;
        }

        /* ── MOBILE DRAWER ── */
        .nav-overlay {
          position: fixed; inset: 0; background: rgba(4,0,51,0.45);
          z-index: 998; opacity: 0; pointer-events: none;
          transition: opacity var(--transition);
        }
        .nav-overlay.open { opacity: 1; pointer-events: all; }

        .nav-drawer {
          position: fixed; top: 0; right: 0; bottom: 0; width: 300px;
          background: var(--nav-bg); backdrop-filter: blur(28px);
          border-left: 1px solid var(--border-t);
          display: flex; flex-direction: column;
          transform: translateX(100%);
          transition: transform 0.42s var(--ease);
          z-index: 999; padding: 5rem 0 2rem;
        }
        .nav-drawer.open { transform: translateX(0); }

        .nav-drawer-link {
          font-family: var(--ff-h); font-size: 1.4rem; font-weight: 400;
          color: var(--ink); padding: 1rem 2.4rem;
          cursor: pointer; background: none;
          border: none; border-bottom: 1px solid var(--border);
          text-align: left; width: 100%;
          transition: color var(--transition), padding-left var(--transition);
        }
        .nav-drawer-link:hover,
        .nav-drawer-link.is-active { color: var(--teal); padding-left: 3rem; }

        .nav-drawer-bottom {
          margin-top: auto; padding: 1.5rem 2.4rem;
          display: flex; gap: 1rem; align-items: center;
          border-top: 1px solid var(--border);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1080px) {
          .nav-links, .nav-cta { display: none !important; }
          .nav-burger { display: flex !important; }
          .nav-inner  { padding: 0 1.5rem; }
        }
        @media (max-width: 600px) { .nav-logo-img { height: 38px; } }
      `}</style>

            <nav className={`nav-root${scrolled ? ' nav-scrolled' : ''}`}>
                <div className="nav-inner">

                    {/* Logo */}
                    <div className="nav-logo" onClick={() => goTo('/')}>
                        <img src={logo} alt="Burj Al Arab Real Estate" className="nav-logo-img" />
                    </div>

                    {/* Desktop links */}
                    <ul className="nav-links">
                        {NAV_LINKS.map(l => (
                            <li key={l.path}>
                                <button
                                    className={`nav-link${isActive(l.path) ? ' is-active' : ''}`}
                                    onClick={() => goTo(l.path)}
                                >
                                    {l.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* ── Theme icon button ── */}
                    {/*<button*/}
                    {/*    className="theme-icon-btn"*/}
                    {/*    onClick={toggleTheme}*/}
                    {/*    aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}*/}
                    {/*>*/}
                    {/*    /!* Moon icon – shown in dark mode *!/*/}
                    {/*    <svg className="t-moon" viewBox="0 0 24 24">*/}
                    {/*        <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />*/}
                    {/*    </svg>*/}
                    {/*    /!* Sun icon – shown in light mode *!/*/}
                    {/*    <svg className="t-sun" viewBox="0 0 24 24">*/}
                    {/*        <circle cx="12" cy="12" r="5" />*/}
                    {/*        <line x1="12" y1="1"     x2="12" y2="3"     />*/}
                    {/*        <line x1="12" y1="21"    x2="12" y2="23"    />*/}
                    {/*        <line x1="4.22" y1="4.22"  x2="5.64" y2="5.64"  />*/}
                    {/*        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />*/}
                    {/*        <line x1="1"  y1="12"    x2="3"  y2="12"    />*/}
                    {/*        <line x1="21" y1="12"    x2="23" y2="12"    />*/}
                    {/*        <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36" />*/}
                    {/*        <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"  />*/}
                    {/*    </svg>*/}
                    {/*</button>*/}

                    {/* CTA */}
                    <button className="nav-cta" onClick={() => goTo('/contact')}>
                        Get in Touch
                    </button>

                    {/* Hamburger */}
                    <button
                        className="nav-burger"
                        onClick={() => setMenuOpen(v => !v)}
                        aria-label="Menu"
                    >
                        <span style={{ transform: menuOpen ? 'rotate(45deg) translate(4px,4px)' : 'none' }} />
                        <span style={{ opacity: menuOpen ? 0 : 1 }} />
                        <span style={{ transform: menuOpen ? 'rotate(-45deg) translate(4px,-4px)' : 'none' }} />
                    </button>
                </div>
            </nav>

            {/* Mobile overlay + drawer */}
            <div className={`nav-overlay${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(false)} />
            <div className={`nav-drawer${menuOpen ? ' open' : ''}`}>
                {NAV_LINKS.map(l => (
                    <button
                        key={l.path}
                        className={`nav-drawer-link${isActive(l.path) ? ' is-active' : ''}`}
                        onClick={() => goTo(l.path)}
                    >
                        {l.label}
                    </button>
                ))}
                <div className="nav-drawer-bottom">
                    {/* Same icon button in the mobile drawer */}
                    <button
                        className="theme-icon-btn"
                        onClick={toggleTheme}
                        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                    >
                        <svg className="t-moon" viewBox="0 0 24 24">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
                        </svg>
                        <svg className="t-sun" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="5" />
                            <line x1="12" y1="1"     x2="12" y2="3"     />
                            <line x1="12" y1="21"    x2="12" y2="23"    />
                            <line x1="4.22" y1="4.22"  x2="5.64" y2="5.64"  />
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                            <line x1="1"  y1="12"    x2="3"  y2="12"    />
                            <line x1="21" y1="12"    x2="23" y2="12"    />
                            <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36" />
                            <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"  />
                        </svg>
                    </button>
                    <button
                        className="nav-cta"
                        style={{ flex: 1, textAlign: 'center' }}
                        onClick={() => goTo('/contact')}
                    >
                        Get in Touch
                    </button>
                </div>
            </div>
        </>
    );
};

export default Navbar;